// StockMetricsInfoTabe.js
import React, { useMemo } from 'react';
import styles from './StockMetricsInfoTabe.module.css';

const formatISODate = (dateStr) => {
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr.slice(0, 10);
  }
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatNumber = (value, digits = 2) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
};

const getLatestByDate = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return rows.reduce((acc, item) => {
    const accTime = acc ? new Date(acc.date).getTime() : -Infinity;
    const itemTime = new Date(item.date).getTime();
    return itemTime > accTime ? item : acc;
  }, null);
};

const StockMetricsInfoTabe = ({
  title = 'Key Metrics',
  subtitle = 'Latest data point',
  columns = [],
  rows = [],
  loading = false
}) => {
  const latest = useMemo(() => getLatestByDate(rows), [rows]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          <div className={styles.subtitle}>{subtitle}</div>
        </div>
        <div className={styles.loading}>Loading chart data...</div>
      </div>
    );
  }

  if (!latest) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          <div className={styles.subtitle}>{subtitle}</div>
        </div>
        <div className={styles.loading}>No data available.</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{title}</div>
        <div className={styles.subtitle}>{subtitle}</div>
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {columns.map((col) => {
                const rawValue = typeof col.getValue === 'function'
                  ? col.getValue(latest)
                  : latest[col.key];

                let displayValue = rawValue;
                if (col.format === 'text') {
                  displayValue = rawValue ?? '—';
                } else if (col.format === 'date' || col.key === 'date') {
                  displayValue = formatISODate(rawValue);
                } else {
                  displayValue = formatNumber(rawValue, col.decimals ?? 2);
                }

                return <td key={col.key}>{displayValue}</td>;
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StockMetricsInfoTabe;
